import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components/LoadingSpinner';
import { icon } from '../shared/components/icons';

// Reports — an index, not an auto-loaded deck (docs/redesign/04 §2).
//
// Pick the artifact first, generate on click: the portfolio deck card is
// the 80% case, owner decks one row each, property reports a searchable
// list. The deck/report renders as a sub-view with a back link — nothing
// heavy loads until asked for.
export class ReportsPage {
  private container: HTMLElement;
  private month: string;
  private index: any = null;
  private view: { kind: 'index' } | { kind: 'deck'; owner: string | null } | { kind: 'property'; id: string; voice: 'owner' | 'rm' } = { kind: 'index' };
  private propSearch = '';
  private deck: import('../shared/components/PortfolioReportDeck').PortfolioReportDeck | null = null;
  private reportDoc: import('../shared/components/PropertyReportDoc').PropertyReportDoc | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    // Reports are about finished periods — default to the last complete month.
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    this.month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  private currentMonth(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  private money(v: any): string { const n = Number(v); return Number.isFinite(n) ? '$' + Math.round(n).toLocaleString() : '—'; }

  async render(): Promise<void> {
    // Owner-ready decks expose portfolio revenue, management fee and owner-net —
    // financial, so Admin + Director only (ground/remote managers see everything
    // else). This tab sits in the Analytics hub, which stays open to all office
    // roles, so the gate lives here.
    let _role = ''; try { _role = sessionStorage.getItem('userRole') || ''; } catch (_) { /* */ }
    if (_role !== 'admin' && _role !== 'director') {
      this.container.innerHTML = `<div class="page-content"><div style="max-width:520px;margin:64px auto;text-align:center;padding:32px;border:1px solid var(--border);border-radius:16px;background:var(--card);">
        <div style="font-size:34px;margin-bottom:10px;">🔒</div>
        <h2 style="margin:0 0 8px;font-size:18px;">Financials are restricted</h2>
        <p style="color:var(--text-muted);font-size:13.5px;line-height:1.55;margin:0;">Owner-ready financial reports — revenue, management fee and owner-net — are limited to Admin and Director roles.</p>
      </div></div>`;
      return;
    }
    // Deep link from Owners ("report →"): jump straight to that owner's deck.
    const pre = sessionStorage.getItem('reports_scope');
    if (pre) {
      sessionStorage.removeItem('reports_scope');
      this.view = pre === 'portfolio' ? { kind: 'deck', owner: null } : { kind: 'deck', owner: pre };
    }
    this.container.innerHTML = `
      <div class="page-content">
        <div class="dash-head">
          <h1>Reports</h1>
          <p>Owner-ready artifacts: the monthly portfolio deck, one deck per owner, and the branded single-property report. Pick what to send, then generate it.</p>
        </div>
        <div id="rp-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div>
      </div>`;
    if (this.view.kind === 'index') {
      await this.loadIndex();
      this.drawIndex();
    } else {
      this.drawSubView();
    }
  }

  private async loadIndex(): Promise<void> {
    try {
      const r = await fetch(`/api/reports/index?month=${this.month}`);
      this.index = r.ok ? await r.json() : null;
    } catch (_) { this.index = null; }
  }

  private drawIndex(): void {
    const body = document.getElementById('rp-body');
    if (!body) return;
    const ix = this.index;
    const p = ix?.portfolio || {};
    const monthLabel = new Date(this.month + '-02').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const owners: any[] = ix?.owners || [];
    const props: any[] = (ix?.properties || []).filter((x: any) =>
      !this.propSearch || (x.name || '').toLowerCase().includes(this.propSearch));

    body.innerHTML = `
      <style>
        .rp-card { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); margin-bottom:14px; box-shadow:var(--shadow-card); overflow:hidden; }
        .rp-eyebrow { font-size:10.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .rp-tbl { width:100%; border-collapse:collapse; font-size:12.5px; }
        .rp-tbl td, .rp-tbl th { padding:10px 16px; border-bottom:1px solid var(--border-soft); text-align:left; }
        .rp-tbl th { font-size:10.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; border-bottom:1px solid var(--border); }
        .rp-tbl tbody tr { transition:background 0.12s ease; }
        .rp-tbl tbody tr:hover { background:var(--bg-subtle); }
        .rp-tbl tbody tr:last-child td { border-bottom:none; }
        .rp-tbl td.r, .rp-tbl th.r { text-align:right; }
        .rp-tbl .mono, .rp-tbl .num, .rp-tbl td.r { font-variant-numeric:tabular-nums; }
        .rp-open { color:var(--info); cursor:pointer; font-weight:600; white-space:nowrap; transition:opacity 0.12s ease; }
        .rp-open:hover { opacity:0.7; }
        .rp-props { display:grid; grid-template-columns:1fr 1fr; gap:0 28px; }
        .rp-prop-row { display:flex; align-items:baseline; gap:8px; padding:8px 0; border-bottom:1px solid var(--border-soft); font-size:12.5px; transition:background 0.12s ease; }
        .rp-prop-row:hover { background:var(--bg-subtle); }
        @media (max-width:760px) { .rp-props { grid-template-columns:1fr; } }
      </style>

      <div style="display:flex; align-items:center; gap:10px; margin-bottom:14px;">
        <label style="font-size:12px; font-weight:600;">Reporting month</label>
        <input type="month" id="rp-month" value="${this.month}" max="${this.currentMonth()}"
               style="padding:6px 10px; border:1px solid var(--border); border-radius:6px; font-size:13px;">
        <button class="de-toggle-btn" id="rp-mtd">This month so far</button>
        <span style="font-size:11px; color:var(--text-muted);">revenue shown on a check-in-month basis</span>
      </div>

      <div class="kpi-section-label">Monthly portfolio deck — ${monthLabel}</div>
      <div class="kpi-grid">
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Revenue</span><span class="kpi-card-ic">${icon('wallet', 17)}</span></div>
          <div class="kpi-card-value">${this.money(p.revenue)}</div>
          <div class="kpi-card-cap">${monthLabel}, check-in-month basis</div>
        </div>
        <div class="kpi-card${p.vs_ly_pct != null && p.vs_ly_pct >= 100 ? ' tone-good' : ''}">
          <div class="kpi-card-top"><span class="kpi-card-label">Vs last year</span><span class="kpi-card-ic">${icon('trending', 17)}</span></div>
          <div class="kpi-card-value">${p.vs_ly_pct != null ? p.vs_ly_pct + '%' : '—'}</div>
          <div class="kpi-card-cap">${p.vs_ly_pct != null ? 'of same month last year' : 'no LY comparison'}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Nights</span><span class="kpi-card-ic">${icon('calendar', 17)}</span></div>
          <div class="kpi-card-value">${(p.nights ?? 0).toLocaleString()}</div>
          <div class="kpi-card-cap">occupied this month</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">Reservations</span><span class="kpi-card-ic">${icon('receipt', 17)}</span></div>
          <div class="kpi-card-value">${(p.reservations ?? 0).toLocaleString()}</div>
          <div class="kpi-card-cap">checking in this month</div>
        </div>
      </div>
      <div class="rp-card" style="padding:14px 20px; display:flex; align-items:center; gap:12px; margin-top:14px;">
        <div style="font-size:13px; color:var(--text-muted);">The branded monthly portfolio deck for ${monthLabel} — ready to generate and send.</div>
        <button class="btn btn-primary btn-sm" id="rp-open-portfolio" style="margin-left:auto;">Open deck →</button>
      </div>

      <div class="pq-card" style="margin-top:18px;">
        <div class="pq-card-h"><div><h3>Owner decks</h3><p>one deck per owner — generate on open</p></div></div>
        ${owners.length ? `<table class="rp-tbl">
          <thead><tr><th>Owner</th><th class="r">Properties</th><th class="r">${monthLabel} revenue</th><th style="color:var(--text-faint,#9ca3af);">Sends to</th><th></th></tr></thead>
          <tbody>${owners.map(o => `<tr>
            <td style="font-weight:600;">${Formatters.escapeHtml(o.name || 'Owner')}</td>
            <td class="r mono num">${o.property_count}</td>
            <td class="r mono num">${this.money(o.revenue)}</td>
            <td style="color:var(--text-faint,#9ca3af); font-size:11px;">${Formatters.escapeHtml(o.email || '—')}</td>
            <td class="r"><span class="rp-open" data-rp-owner="${Formatters.escapeHtml(String(o.id))}">Open deck →</span></td>
          </tr>`).join('')}</tbody>
        </table>` : `<div style="padding:14px 20px; font-size:12px; color:var(--text-muted);">No owners with assigned properties yet — assign them under Admin ▸ Owners.</div>`}
      </div>

      <div class="pq-card" style="margin-top:18px;">
        <div class="pq-card-h">
          <div><h3>Property reports</h3><p>the branded single-property artifact — owner voice hides internals, RM voice shows them</p></div>
          <input id="rp-prop-search" type="search" placeholder="Search…" value="${Formatters.escapeHtml(this.propSearch)}"
                 style="font-size:12px; padding:5px 10px; border:1px solid var(--border); border-radius:6px; width:180px;">
        </div>
        <div class="rp-props" style="padding:12px 20px 16px;">
          ${props.map(x => `<div class="rp-prop-row">
            <span style="font-weight:600; flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${Formatters.escapeHtml(x.name || '')}">${Formatters.escapeHtml(x.name || x.id)}</span>
            <span style="color:var(--text-muted); font-size:11px;">${Formatters.escapeHtml(x.owner_name || '')}</span>
            <span class="rp-open" data-rp-prop="${x.id}" data-rp-voice="owner">owner view</span>
            <span class="rp-open" data-rp-prop="${x.id}" data-rp-voice="rm">RM view</span>
          </div>`).join('') || '<div style="font-size:12px; color:var(--text-muted); padding:8px 0;">No matches.</div>'}
        </div>
      </div>`;
    this.bindIndex();
  }

  private bindIndex(): void {
    document.getElementById('rp-month')?.addEventListener('change', async (e) => {
      const v = (e.target as HTMLInputElement).value;
      if (!v) return;
      this.month = v;
      await this.loadIndex();
      this.drawIndex();
    });
    document.getElementById('rp-mtd')?.addEventListener('click', async () => {
      this.month = this.currentMonth();
      await this.loadIndex();
      this.drawIndex();
    });
    document.getElementById('rp-prop-search')?.addEventListener('input', (e) => {
      this.propSearch = (e.target as HTMLInputElement).value.toLowerCase();
      this.drawIndex();
      const inp = document.getElementById('rp-prop-search') as HTMLInputElement | null;
      if (inp) { inp.focus(); inp.setSelectionRange(inp.value.length, inp.value.length); }
    });
    document.getElementById('rp-open-portfolio')?.addEventListener('click', () => {
      this.view = { kind: 'deck', owner: null }; this.drawSubView();
    });
    document.querySelectorAll<HTMLElement>('[data-rp-owner]').forEach(el =>
      el.addEventListener('click', () => { this.view = { kind: 'deck', owner: el.dataset.rpOwner! }; this.drawSubView(); }));
    document.querySelectorAll<HTMLElement>('[data-rp-prop]').forEach(el =>
      el.addEventListener('click', () => {
        this.view = { kind: 'property', id: el.dataset.rpProp!, voice: (el.dataset.rpVoice as 'owner' | 'rm') || 'owner' };
        this.drawSubView();
      }));
  }

  // Month → calendar window: full month for past months, MTD for the current
  // one (no on-the-books future nights in a performance report).
  private monthWindow(): { start: string; end: string } | null {
    const m = this.month.match(/^(\d{4})-(\d{2})$/);
    if (!m) return null;
    const y = +m[1], mo = +m[2];
    const today = new Date();
    const isCurrent = today.getFullYear() === y && today.getMonth() + 1 === mo;
    const lastDay = isCurrent ? today.getDate() : new Date(y, mo, 0).getDate();
    return { start: `${this.month}-01`, end: `${this.month}-${String(lastDay).padStart(2, '0')}` };
  }

  private async drawSubView(): Promise<void> {
    const body = document.getElementById('rp-body');
    if (!body) return;
    const back = document.createElement('div');
    back.innerHTML = `<button class="btn btn-secondary btn-sm" id="rp-back" style="margin-bottom:12px;">← All reports</button>`;
    body.innerHTML = '';
    body.appendChild(back);
    const host = document.createElement('div');
    host.innerHTML = `<div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div>`;
    body.appendChild(host);
    document.getElementById('rp-back')?.addEventListener('click', async () => {
      this.deck?.destroy(); this.deck = null;
      this.reportDoc?.destroy(); this.reportDoc = null;
      this.view = { kind: 'index' };
      await this.loadIndex();
      this.drawIndex();
    });
    if (this.view.kind === 'deck') {
      const { PortfolioReportDeck } = await import('../shared/components/PortfolioReportDeck');
      this.deck?.destroy();
      this.deck = new PortfolioReportDeck(this.view.owner);
      const w = this.monthWindow();
      if (w) this.deck.setWindow(w.start, w.end);
      await this.deck.render(host);
    } else if (this.view.kind === 'property') {
      const { PropertyReportDoc } = await import('../shared/components/PropertyReportDoc');
      this.reportDoc?.destroy();
      this.reportDoc = new PropertyReportDoc(this.view.id, this.view.voice);
      await this.reportDoc.render(host);
    }
  }
}
